import React, { useEffect, useRef, useState } from 'react';
import { cn, TIMELINE_ICONS, formatTime } from '@/lib/utils';
import {
  Search, Brain, Globe, Shield, Send, Inbox, Clock, Play,
  CheckCircle, XCircle, AlertTriangle, Info, Lightbulb, Ban, Circle,
  Bug, Terminal, Filter
} from 'lucide-react';

const iconMap = {
  Search, Brain, Globe, Shield, Send, Inbox, Clock, Play,
  CheckCircle, XCircle, AlertTriangle, Info, Lightbulb, Ban, Circle,
  Bug, Terminal
};

const actionColors = {
  success: 'text-emerald-400 border-emerald-400/30',
  failed: 'text-red-400 border-red-400/30',
  error: 'text-red-400 border-red-400/30',
  warning: 'text-amber-400 border-amber-400/30',
  research: 'text-blue-400 border-blue-400/30',
  knowledge: 'text-purple-400 border-purple-400/30',
  browser: 'text-cyan-400 border-cyan-400/30',
  captcha: 'text-yellow-400 border-yellow-400/30',
  email_sent: 'text-teal-400 border-teal-400/30',
  email_received: 'text-teal-300 border-teal-300/30',
  waiting: 'text-amber-400 border-amber-400/30',
  trying: 'text-blue-300 border-blue-300/30',
  info: 'text-gray-400 border-gray-400/30',
  learning: 'text-violet-400 border-violet-400/30',
  cancelled: 'text-gray-500 border-gray-500/30',
  debug: 'text-zinc-500 border-zinc-500/30',
  adaptive: 'text-orange-400 border-orange-400/30',
};

// Debug-level entries only shown with filter
const debugActions = new Set(['debug']);

export function RealtimeTimeline({ entries = [], autoScroll = true, isAdmin = false }) {
  const scrollRef = useRef(null);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, autoScroll]);

  // Filter entries based on showDebug toggle
  const filteredEntries = showDebug
    ? entries
    : entries.filter(e => !debugActions.has(e.action));

  const debugCount = entries.filter(e => debugActions.has(e.action)).length;

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        No timeline entries yet...
      </div>
    );
  }

  return (
    <div>
      {/* Admin: Debug filter toggle */}
      {isAdmin && debugCount > 0 && (
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
          <span className="text-xs text-muted-foreground">
            {filteredEntries.length} of {entries.length} entries
          </span>
          <button
            onClick={() => setShowDebug(prev => !prev)}
            className={cn(
              'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors',
              showDebug
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            )}
            data-testid="toggle-debug-log"
          >
            <Bug className="w-3 h-3" />
            {showDebug ? 'Hide Debug' : `Show Debug (${debugCount})`}
          </button>
        </div>
      )}

      <div
        ref={scrollRef}
        className="timeline-scroll overflow-y-auto max-h-[600px] pr-2"
        data-testid="timeline-feed"
      >
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />

          <div className="space-y-1">
            {filteredEntries.map((entry, idx) => {
              const isDebugEntry = debugActions.has(entry.action);
              const iconName = entry.action === 'debug' ? 'Terminal'
                : entry.action === 'warning' ? 'AlertTriangle'
                : TIMELINE_ICONS[entry.action] || 'Circle';
              const IconComp = iconMap[iconName] || Circle;
              const colors = actionColors[entry.action] || 'text-gray-400 border-gray-400/30';

              return (
                <div
                  key={entry.id || idx}
                  className={cn(
                    'relative flex items-start gap-3 pl-0 py-1.5 group',
                    isDebugEntry && 'opacity-60'
                  )}
                  data-testid={`timeline-entry-${idx}`}
                >
                  {/* Icon */}
                  <div className={cn(
                    'relative z-10 flex items-center justify-center w-[30px] h-[30px] rounded-full border bg-background flex-shrink-0',
                    colors
                  )}>
                    <IconComp className="w-3.5 h-3.5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-xs font-semibold uppercase tracking-wide', colors.split(' ')[0])}>
                        {entry.action}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">
                        {formatTime(entry.time)}
                      </span>
                    </div>
                    <p className={cn(
                      'text-sm mt-0.5 break-words',
                      isDebugEntry ? 'text-foreground/50 font-mono text-xs' : 'text-foreground/80'
                    )}>
                      {entry.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
